<template>
  <div class="md-content" v-html="html" @click="onContentClick" />

  <Teleport to="body">
    <div v-if="fullscreenTable" class="md-table-modal" @click.self="closeFullscreen">
      <div class="md-table-modal-panel">
        <header class="md-table-modal-head">
          <span class="md-table-modal-title">Tableau</span>
          <button type="button" class="md-table-modal-close" title="Fermer" @click="closeFullscreen">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div class="md-table-modal-body" v-html="fullscreenTable" />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

const props = defineProps<{ content: string }>()

// Barre d'outils injectee au-dessus de chaque tableau : un tableau large ne doit
// jamais elargir la bulle, et doit pouvoir s'ouvrir en grand.
const TABLE_TOOLBAR = [
  '<div class="md-table-bar">',
  '<button type="button" class="md-table-fs" data-md-table-fullscreen title="Afficher en plein écran" aria-label="Afficher le tableau en plein écran">',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">',
  '<path stroke-linecap="round" stroke-linejoin="round" d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />',
  '</svg><span>Plein écran</span></button></div>',
].join('')

// Le texte vient d'un modele : on rend le Markdown PUIS on assainit le HTML.
// Sans cette etape, une reponse contenant du HTML brut pourrait injecter du script.
const html = computed(() => {
  const source = props.content ?? ''
  if (!source.trim()) return ''
  let rendered: string
  try {
    rendered = marked.parse(source, { gfm: true, breaks: true }) as string
  } catch {
    // Markdown malforme : on retombe sur du texte echappe, jamais sur du HTML brut.
    return `<p>${escapeHtml(source).replace(/\n/g, '<br>')}</p>`
  }
  const clean = DOMPurify.sanitize(rendered, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'del', 'code', 'pre', 'blockquote',
      'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'img',
    ],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'colspan', 'rowspan', 'align', 'src', 'alt'],
    // Autorise les data: (pièces jointes images retournées en base64) en plus des protocoles sûrs.
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  })
  // Encadre chaque tableau : barre d'outils (hors zone de defilement) + zone scrollable.
  return clean
    .replace(/<table>/g, `<div class="md-table-block">${TABLE_TOOLBAR}<div class="md-table-wrap"><table>`)
    .replace(/<\/table>/g, '</table></div></div>')
})

// La barre est du HTML injecte : on passe par delegation d'evenements.
const fullscreenTable = ref<string | null>(null)

function onContentClick(event: MouseEvent) {
  const target = (event.target as HTMLElement | null)?.closest?.('[data-md-table-fullscreen]')
  if (!target) return
  const table = target.closest('.md-table-block')?.querySelector('table')
  if (!table) return
  fullscreenTable.value = DOMPurify.sanitize(table.outerHTML, {
    ALLOWED_TAGS: ['table', 'thead', 'tbody', 'tr', 'th', 'td'],
    ALLOWED_ATTR: ['colspan', 'rowspan', 'align'],
  })
}

const closeFullscreen = () => { fullscreenTable.value = null }

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && fullscreenTable.value) closeFullscreen()
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

function escapeHtml(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
</script>

<style scoped>
.md-content {
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}

.md-content :deep(> :first-child) { margin-top: 0; }
.md-content :deep(> :last-child) { margin-bottom: 0; }

.md-content :deep(p) { margin: 0.4rem 0; }
.md-content :deep(h1),
.md-content :deep(h2),
.md-content :deep(h3),
.md-content :deep(h4) {
  margin: 0.7rem 0 0.35rem;
  font-weight: 700;
  line-height: 1.3;
}
.md-content :deep(h1) { font-size: 1.05rem; }
.md-content :deep(h2) { font-size: 1rem; }
.md-content :deep(h3) { font-size: 0.95rem; }
.md-content :deep(h4) { font-size: 0.9rem; }

.md-content :deep(ul),
.md-content :deep(ol) {
  margin: 0.4rem 0;
  padding-left: 1.15rem;
}
.md-content :deep(ul) { list-style: disc; }
.md-content :deep(ol) { list-style: decimal; }
.md-content :deep(li) { margin: 0.15rem 0; }

.md-content :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}

.md-content :deep(code) {
  background: rgba(17, 24, 39, 0.08);
  border-radius: 4px;
  padding: 0.08rem 0.28rem;
  font-size: 0.82em;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.md-content :deep(pre) {
  margin: 0.5rem 0;
  padding: 0.5rem 0.65rem;
  background: #111827;
  color: #f9fafb;
  border-radius: 8px;
  overflow-x: auto;
}
.md-content :deep(pre code) {
  background: transparent;
  padding: 0;
  color: inherit;
  font-size: 0.78rem;
  white-space: pre;
}

.md-content :deep(blockquote) {
  margin: 0.5rem 0;
  padding-left: 0.6rem;
  border-left: 3px solid #d1d5db;
  color: #4b5563;
}

.md-content :deep(hr) {
  margin: 0.75rem 0;
  border: 0;
  border-top: 1px solid #e5e7eb;
}

/* --- Tableaux ------------------------------------------------------------ */
/* Le bloc fournit le cadre ; la barre reste fixe au-dessus du defilement. */
.md-content :deep(.md-table-block) {
  margin: 0.55rem 0;
  max-width: 100%;
  overflow: hidden;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
}

.md-content :deep(.md-table-bar) {
  display: flex;
  justify-content: flex-end;
  padding: 2px 4px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.md-content :deep(.md-table-fs) {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: #4b5563;
  font-size: 10.5px;
  line-height: 1.2;
  cursor: pointer;
}
.md-content :deep(.md-table-fs:hover) { background: #e5e7eb; color: #111827; }
.md-content :deep(.md-table-fs svg) { width: 12px; height: 12px; }

/* Zone de defilement horizontale : le tableau garde sa largeur naturelle. */
.md-content :deep(.md-table-wrap) {
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Largeur intrinseque : les colonnes prennent la taille de leur contenu
   au lieu d'etre etirees a 100 % du panneau.
   Les regles sont mutualisees : le tableau en plein ecran est identique
   a celui de la conversation (memes couleurs, marges et alignements). */
.md-content :deep(table) {
  width: max-content;
  border-collapse: collapse;
  font-size: 12.5px;
}
/* Plein ecran : largeur fixe sur l'ecran. Le texte trop long revient a la ligne
   dans les cellules au lieu de deborder horizontalement. */
.md-table-modal-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

.md-content :deep(thead th) {
  background: #f3f4f6;
  color: #111827;
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
  border-bottom: 1px solid #d1d5db;
}
/* Plein ecran : l'entete se retourne a la ligne si besoin (largeur fixe). */
.md-table-modal-body :deep(thead th) {
  background: #f3f4f6;
  color: #111827;
  font-weight: 600;
  text-align: left;
  white-space: normal;
  border-bottom: 1px solid #d1d5db;
  position: sticky;
  top: 0;
}

.md-content :deep(th),
.md-content :deep(td),
.md-table-modal-body :deep(th),
.md-table-modal-body :deep(td) {
  padding: 5px 8px;
  border-right: 1px solid #e5e7eb;
  vertical-align: top;
}
/* Plein ecran : retour a la ligne force dans les cellules (largeur fixe d'ecran). */
.md-table-modal-body :deep(th),
.md-table-modal-body :deep(td) {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* Plein ecran : premiere colonne ajustee a son contenu (date, heure, id...).
   Elle garde sa largeur naturelle sans retour a la ligne, le reste des
   colonnes partage l'espace disponible jusqu'au bord de l'ecran. */
.md-table-modal-body :deep(th:first-child),
.md-table-modal-body :deep(td:first-child) {
  width: max-content;
  min-width: max-content;
  white-space: nowrap;
  padding-right: 10px;
}

.md-content :deep(th:last-child),
.md-content :deep(td:last-child),
.md-table-modal-body :deep(th:last-child),
.md-table-modal-body :deep(td:last-child) { border-right: 0; }

.md-content :deep(tbody tr),
.md-table-modal-body :deep(tbody tr) { border-bottom: 1px solid #e5e7eb; }

.md-content :deep(tbody tr:last-child),
.md-table-modal-body :deep(tbody tr:last-child) { border-bottom: 0; }

.md-content :deep(tbody tr:nth-child(even)),
.md-table-modal-body :deep(tbody tr:nth-child(even)) { background: #fafafa; }

.md-content :deep(td),
.md-table-modal-body :deep(td) { color: #1f2937; }

/* --- Plein ecran --------------------------------------------------------- */
.md-table-modal {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(17, 24, 39, 0.55);
}

.md-table-modal-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  border-radius: 0;
  background: #fff;
  box-shadow: none;
}

.md-table-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.md-table-modal-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #111827;
}

.md-table-modal-close {
  display: inline-flex;
  padding: 4px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #4b5563;
  cursor: pointer;
}
.md-table-modal-close:hover { background: #e5e7eb; color: #111827; }
.md-table-modal-close svg { width: 18px; height: 18px; }

.md-table-modal-body {
  flex: 1;
  overflow: auto;
  padding: 0.75rem;
}

/* En-tete collant, uniquement en plein ecran : confort de lecture pendant
   le defilement vertical. Le reste du style vient des regles mutualisees. */
.md-table-modal-body :deep(thead th) {
  position: sticky;
  top: 0;
}

/* Images de pièces jointes affichées directement dans la bulle de conversation. */
.md-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  display: block;
  margin: 0.5rem 0;
  background: #f9fafb;
}
</style>
